'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  Language,
  DEFAULT_LANGUAGE,
  getStoredLanguage,
  setStoredLanguage,
  getLanguageDirection,
  getTranslations,
  getAILanguageInstruction,
  TranslationKeys,
} from '@/lib/i18n';

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationKeys;
  dir: 'ltr' | 'rtl';
  /** Prepend this to any AI prompt to ensure output is in the user's language */
  aiLanguageInstruction: string;
}

const defaultTranslations = getTranslations(DEFAULT_LANGUAGE);

const LanguageContext = createContext<LanguageContextValue>({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: defaultTranslations,
  dir: 'ltr',
  aiLanguageInstruction: '',
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(DEFAULT_LANGUAGE);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Read from localStorage on mount — ensures persistence after refresh/login
    const stored = getStoredLanguage();
    setLanguageState(stored);
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    const dir = getLanguageDirection(language);
    document.documentElement.setAttribute('lang', language);
    document.documentElement.setAttribute('dir', dir);
    // Dispatch a custom event so any component listening can react
    window.dispatchEvent(new CustomEvent('aimaker-language-change', { detail: { language } }));
  }, [language, mounted]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    setStoredLanguage(lang);
  }, []);

  // Recompute translations synchronously on every render — no stale closure
  const t = getTranslations(language) ?? defaultTranslations;
  const dir = getLanguageDirection(language);
  const aiLanguageInstruction = getAILanguageInstruction(language);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, dir, aiLanguageInstruction }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
